import * as React from 'react';
import { connect } from 'react-redux';
import type { HandleThunkActionCreator } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import type UserGroupRequestModel from 'ab-requestModels/userGroup.requestModel';

import type { RootState } from 'af-reducers';

import { SPECIAL_EMAIL_NOTIFICATIONS } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import type * as CompanyActions from 'af-actions/companies';

import GeneralNotificationsForm from './Form';
import { validate } from './validation';
import type { SpecialEmailsForm } from '../formModel';
import { SpecialEmails } from '../formModel';

interface OwnProps {
	isVisible: boolean;
	userGroupsForCompany: UserGroupRequestModel[];
	editSpecialEmailNotifications: HandleThunkActionCreator<typeof CompanyActions.editSpecialEmailNotifications>;
	toggleEdit: () => void;
	initialValues?: Nullable<FormProps['initialValues']>;
}

type ConnectedOwnProps = OwnProps & ConnectedProps<typeof connector>;
type FormProps = InjectedFormProps<SpecialEmailsForm, ConnectedOwnProps>;
type Props = ConnectedOwnProps & FormProps;

const SpecialEmailsComponent: React.FC<Props> = (props) => {
	const { toggleEdit, editSpecialEmailNotifications, handleSubmit, isVisible, invalid, submitting, reset } = props;

	const editCompanyFeatureFlags = React.useCallback(async (form: SpecialEmailsForm) => {
		await editSpecialEmailNotifications(SpecialEmails.fromForm(form));
		toggleEdit();
	}, [editSpecialEmailNotifications, toggleEdit]);

	const onClose = React.useCallback(() => {
		reset();
		toggleEdit();
	}, [reset, toggleEdit]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={isVisible}
			size="xxl"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="Edit Special Emails"
			/>
			<CustomModal.Body padding="none">
				<GeneralNotificationsForm
					{...props}
					formName={SPECIAL_EMAIL_NOTIFICATIONS}
					onSubmit={editCompanyFeatureFlags}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(editCompanyFeatureFlags)}
					reduxFormSubmitting={submitting}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData } } = state;
	if (!companyData) {
		throw new Error('Company Data not present.');
	}

	return {
		emailDomain: companyData.companyEmailDomain,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	reduxForm<SpecialEmailsForm, ConnectedOwnProps>({
		form: SPECIAL_EMAIL_NOTIFICATIONS,
		enableReinitialize: true,
		validate,
	})
);

export default enhance(SpecialEmailsComponent);
